import React, { useContext } from 'react';
import Helmet from 'react-helmet';
import {
  Grid,
  createStyles,
  makeStyles,
  Theme,
  Typography,
  Box,
  Container,
} from '@material-ui/core';
import { graphql } from 'gatsby';
import { getBodyComponent } from '../util/contentful-render-utils';
import Divider from '@material-ui/core/Divider';
// eslint-disable-next-line import/no-unassigned-import
import './css/contentful-pages.css';
import PageTitle from '../components/blog/page-title';
import SlugLink from '../components/slug-link';
import RichText, { richTextStyles } from '../components/rich-text';
import {
  BlogArticleFragment,
  BlogTopicFragment,
} from '../__generated__/gatsby-types';
import colors from '../themes/main-colors';
import Categories from '../components/blog/categories';
import { useTranslation } from '../hooks/useTranslation';
import { usePathContext } from '../providers/PathProvider';
import SocialShareLinks from '../components/blog/social-share-links';
import {
  LanguageContext,
  DEFAULT_LANGUAGE,
} from '../providers/LanguageProvider';
import { useIsMobile } from '../util/style-utils';
import PreviousNext from '../components/blog/previous-next';
import RelatedTopics from '../components/blog/related-topics';
import ArticleRelatedPosts from '../components/blog/article-related-posts';
import { useBlogBlurb, BlogBlurb } from '../components/blog/blog-blurb';

type Props = {
  currentPost?: BlogArticleFragment;
};

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    rootContainer: {
      [theme.breakpoints.up('md')]: {
        marginTop: '30px',
        padding: '0 25px 60px',
      },
      [theme.breakpoints.down('sm')]: {
        padding: '30px 20px 0',
      },
      '& .RichText': {
        '& .Column': {
          padding: '20px 0',
          '& .WrapperImage picture, .WrapperImage img': {
            marginTop: 0,
          },
        },
      },
    },
    navLine: {
      marginBottom: 0,
      color: `${colors.noirBlur}`,
      backgroundColor: `${colors.noirBlur}`,
    },
    subTitle: {
      fontSize: '14px',
      margin: '10px 0 0',
    },
    image: {
      width: '100%',
      height: 'auto',
      objectFit: ({ currentPost }: Props) =>
        currentPost?.imageFocusHorizontal || currentPost?.imageFocusVertical
          ? 'cover'
          : 'fill',
      objectPosition: ({ currentPost }: Props) =>
        `${currentPost?.imageFocusHorizontal ||
          '50%'} ${currentPost?.imageFocusVertical || '50%'}`,
      marginTop: 30,
      [theme.breakpoints.down('sm')]: {
        width: '100%',
        height: '600px',
      },
    },
    smallHeading: {
      fontSize: theme.spacing(1.75),
      fontWeight: 700,
      textTransform: 'uppercase',
    },
    publishedBox: {
      [theme.breakpoints.down('sm')]: {
        flexDirection: 'column',
        alignItems: 'start',
      },
    },
    category: {
      marginBottom: '20px',
    },
    stickySection: {
      top: 32,
      position: 'sticky',
    },
    divider: {
      color: colors.lightGray1,
      backgroundColor: colors.lightGray1,
      borderColor: colors.lightGray1,
      borderStyle: 'solid',
      margin: '30px 0',
    },
    narrowDivider: {
      color: colors.lightGray1,
      backgroundColor: colors.lightGray1,
      borderColor: colors.lightGray1,
      borderStyle: 'solid',
    },
    publishDivider: {
      width: '1px',
      height: '23px',
      backgroundColor: colors.lightGray1,
      margin: '0 8px',
    },
    descriptionText: {
      fontSize: '16px',
      fontStyle: 'italic',
      [theme.breakpoints.down('sm')]: {
        padding: '30px 20px',
      },
    },
    articlePublishDate: {
      fontSize: '18px',
      whiteSpace: 'nowrap',
    },
    articleSlug: {
      fontSize: '18px',
      fontWeight: 'bold',
    },
    borderLine: {
      borderTop: `1px solid ${colors.gray82}`,
    },
  }),
);

export const BlogArticleTemplate = ({ pageContext, data }: any) => {
  const { t, inlineRichT } = useTranslation();
  const isMobile = useIsMobile();
  const richText = richTextStyles();
  const { resolve } = usePathContext();
  const { language } = useContext(LanguageContext);
  const pageContextInfo = pageContext[language || 'en'] || pageContext['es'];
  const { showBlogBlurb } = useBlogBlurb();

  // All posts by language
  const allPosts: BlogArticleFragment[] = data.allPosts?.nodes.filter(
    // (node: BlogArticleFragment) => node.node_locale === language,
    (node: BlogArticleFragment) => node.node_locale === DEFAULT_LANGUAGE, // Temporarily disable localization on public pages.
  );

  // Current post
  const currentPostIndex = allPosts?.findIndex(
    (post: BlogArticleFragment) =>
      post?.contentful_id === pageContextInfo.contentful_id,
  );
  const currentPost = allPosts[currentPostIndex];

  const classes = useStyles({ currentPost });

  // Previous & Next posts are not filtered by category - check all posts.
  const previousPost =
    currentPostIndex > 0 ? allPosts[currentPostIndex - 1] : undefined;
  const nextPost =
    currentPostIndex < allPosts.length - 1
      ? allPosts[currentPostIndex + 1]
      : undefined;

  // Related posts are in the same category as the current post
  const relatedPosts = allPosts
    .filter(
      (post: BlogArticleFragment) =>
        post?.category?.contentful_id ===
          currentPost?.category?.contentful_id &&
        post?.contentful_id !== currentPost.contentful_id,
    )
    .slice(0, 3);

  return (
    <>
      <Helmet bodyAttributes={{ class: 'TemplatePage' }}>
        <title>{currentPost?.browserTitle || currentPost?.title}</title>
        {currentPost?.metaDescriptionSeo && (
          <meta name="description" content={currentPost.metaDescriptionSeo} />
        )}
      </Helmet>
      {!isMobile && <hr className={classes.navLine} />}
      <Container maxWidth="lg" style={{ paddingRight: 0, paddingLeft: 0 }}>
        <Categories currentArticleCategoryName={currentPost?.category?.name} />
      </Container>
      <Box className={classes.borderLine}>
        <Container maxWidth="lg" className={classes.rootContainer}>
          <Grid container spacing={isMobile ? 0 : 7}>
            <Grid item xs={12} md={8}>
              <PageTitle title={currentPost.title!} postTitle subHeading />
              {currentPost.formattedUpdateDate && (
                <Typography variant={'body1'} className={classes.subTitle}>
                  {inlineRichT('ARTICLE_UPDATED_ON', {
                    UPDATED_DATE: currentPost.formattedUpdateDate,
                  })}
                </Typography>
              )}
              {currentPost?.heroImage?.file?.url && (
                <img
                  alt=""
                  className={classes.image}
                  src={currentPost?.heroImage?.file?.url}
                />
              )}
              {getBodyComponent(currentPost?.contentBody)}
              <hr className={classes.divider} />
              {!isMobile && (
                <>
                  <SocialShareLinks
                    title={currentPost?.browserTitle || currentPost?.title!}
                    slug={resolve(currentPost.slug)}
                  />
                  {showBlogBlurb && (
                    <>
                      <hr className={classes.divider} />
                      <BlogBlurb cssClass={classes.descriptionText} />
                    </>
                  )}
                  <PreviousNext previous={previousPost} next={nextPost} />
                </>
              )}
            </Grid>
            <Grid item xs={12} md={4}>
              {currentPost.formattedPublishDate && (
                <>
                  <Typography
                    variant={'body1'}
                    className={classes.smallHeading}
                  >
                    {t('PUBLISHED')}
                  </Typography>
                  <Box
                    display="flex"
                    alignItems="center"
                    className={classes.publishedBox}
                    marginBottom={3}
                  >
                    {currentPost?.formattedPublishDate && (
                      <Typography className={classes.articlePublishDate}>
                        {currentPost?.formattedPublishDate}
                      </Typography>
                    )}
                    {currentPost?.author?.name && (
                      <>
                        {!isMobile && (
                          <Divider className={classes.publishDivider} />
                        )}
                        <SlugLink
                          slug={currentPost?.author?.slug}
                          className={classes.articleSlug}
                          linkText={currentPost?.author?.name}
                        />
                      </>
                    )}
                  </Box>
                </>
              )}
              {currentPost?.category && (
                <div className={classes.category}>
                  <Typography
                    variant={'body1'}
                    className={classes.smallHeading}
                  >
                    {t('CATEGORY')}
                  </Typography>
                  <SlugLink
                    className={classes.articleSlug}
                    slug={currentPost.category.slug}
                    linkText={currentPost?.category?.name?.toUpperCase()}
                  />
                </div>
              )}
              {!isMobile && (
                <>
                  {currentPost?.topics && (
                    <RelatedTopics
                      topics={currentPost?.topics as BlogTopicFragment[]}
                      title={t('ARTICLE_TOPICS')}
                    />
                  )}
                  {relatedPosts.length > 0 && (
                    <div className={classes.stickySection}>
                      <hr className={classes.divider} />
                      <Typography
                        variant={'body1'}
                        className={classes.smallHeading}
                      >
                        {t('RELATED_POSTS')}
                      </Typography>
                      {relatedPosts?.map((relatedPost: BlogArticleFragment) => (
                        <ArticleRelatedPosts relatedPost={relatedPost} />
                      ))}
                    </div>
                  )}
                </>
              )}
              {isMobile && (
                <>
                  <hr className={classes.divider} />
                  <SocialShareLinks
                    title={currentPost?.browserTitle || currentPost?.title!}
                    slug={resolve(currentPost.slug)}
                  />
                  <hr className={classes.divider} />
                  {relatedPosts.length > 0 && (
                    <div>
                      <Typography
                        variant={'body1'}
                        className={classes.smallHeading}
                      >
                        {t('RELATED_POSTS')}
                      </Typography>
                      {relatedPosts?.map((relatedPost: BlogArticleFragment) => (
                        <ArticleRelatedPosts relatedPost={relatedPost} />
                      ))}
                    </div>
                  )}
                  {currentPost?.topics && (
                    <RelatedTopics
                      topics={currentPost?.topics as BlogTopicFragment[]}
                      title={t('ARTICLE_TOPICS')}
                    />
                  )}
                </>
              )}
            </Grid>
          </Grid>
        </Container>
      </Box>
      {isMobile && (
        <>
          <PreviousNext previous={previousPost} next={nextPost} />
          {showBlogBlurb && <BlogBlurb cssClass={classes.descriptionText} />}
        </>
      )}
    </>
  );
};
export default BlogArticleTemplate;

export const query = graphql`
  query PostsPageQuery {
    allPosts: allContentfulArticle(
      filter: { slug: { ne: null, regex: "/^((?!placeholder).)*$/i" } }
      sort: { fields: [publishDate, updateDate], order: [DESC, DESC] }
    ) {
      nodes {
        ...BlogArticle
      }
    }
  }
`;
